import * as React from "react";
import { findScrollParent } from "../../hooks/useFillViewportHeight";

/**
 * Kompakter Kontaktkopf beim Scrollen auf dem Handy (T-146).
 *
 * Der Kontaktkopf (`.dhead`) klebt auch unter 768px oben, damit das Schloss zum
 * Bearbeiten immer erreichbar bleibt. Voll aufgeklappt belegte er dort aber gut
 * ein Drittel des Schirms. Deshalb klappt er sich auf dem Handy beim Scrollen
 * selbst zusammen (Verantwortlichen-Pillen und Chip-Zeile aus) und ist ganz oben
 * wieder vollständig.
 *
 * Hysterese ist Pflicht, nicht Kosmetik: Beim Zusammenklappen schrumpft der Kopf,
 * der Inhalt rückt nach oben, und Browser mit Scroll-Anchoring (Edge/Chrome)
 * ziehen die Scrollposition um genau diese Höhe zurück. Läge die Schwelle zum
 * Aufklappen darüber, klappte der Kopf sofort wieder auf — und wieder zu, im
 * Wechsel. Darum wird erst zusammengeklappt, wenn die Scrollhöhe die wegfallende
 * Höhe plus Puffer übersteigt, und erst ganz oben wieder aufgeklappt.
 */

/** Puffer über der wegfallenden Höhe, ab dem zusammengeklappt wird (px). */
export const KOMPAKT_PUFFER = 48;
/** Unterhalb dieser Scrollhöhe ist der Kopf wieder vollständig (px). */
export const VOLL_UNTER = 8;
/** Ab dieser Breite gelten die Desktop-Regeln — dort bleibt der Kopf, wie er ist. */
export const HANDY_MEDIA = "(max-width: 768px)";

/**
 * Nächster Zustand aus aktuellem Zustand, Scrollhöhe und der Höhe, um die der
 * Kopf beim Zusammenklappen schrumpft.
 */
export function naechsterKompaktZustand(kompakt: boolean, scrollY: number, wegfallend: number): boolean {
  if (kompakt) return scrollY >= VOLL_UNTER;
  return scrollY > Math.max(0, wegfallend) + KOMPAKT_PUFFER;
}

/** Höhe der Bänder, die im kompakten Kopf verschwinden (inkl. Außenabstände). */
function wegfallendeHoehe(kopf: HTMLElement): number {
  let summe = 0;
  for (const band of Array.from(kopf.querySelectorAll<HTMLElement>(":scope > .verantrow, :scope > .metarow"))) {
    const stil = window.getComputedStyle(band);
    summe += band.offsetHeight + (parseFloat(stil.marginTop) || 0) + (parseFloat(stil.marginBottom) || 0);
  }
  return summe;
}

export function useKopfKompaktBeimScrollen(kopfRef: React.RefObject<HTMLElement>, aktiv: boolean): boolean {
  const [kompakt, setKompakt] = React.useState(false);

  React.useEffect(() => {
    const kopf = kopfRef.current;
    if (!aktiv || !kopf || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setKompakt(false);
      return;
    }
    const mq = window.matchMedia(HANDY_MEDIA);
    const scroller = findScrollParent(kopf);
    const scrollY = () => (scroller ? scroller.scrollTop : window.scrollY);
    let jetzt = false;
    const setze = (wert: boolean) => {
      if (wert === jetzt) return;
      jetzt = wert;
      setKompakt(wert);
    };
    const pruefen = () => {
      if (!mq.matches) {
        setze(false);
        return;
      }
      // Die wegfallende Höhe nur im vollen Zustand messen — im kompakten sind die
      // Bänder ausgeblendet und hätten Höhe 0.
      setze(naechsterKompaktZustand(jetzt, scrollY(), jetzt ? 0 : wegfallendeHoehe(kopf)));
    };

    pruefen();
    const ziel: HTMLElement | Window = scroller ?? window;
    ziel.addEventListener("scroll", pruefen, { passive: true });
    mq.addEventListener?.("change", pruefen);
    return () => {
      ziel.removeEventListener("scroll", pruefen);
      mq.removeEventListener?.("change", pruefen);
    };
  }, [kopfRef, aktiv]);

  return kompakt;
}
